import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Lock } from "lucide-react";
import { useJoinPrice } from "@/hooks/useJoinPrice";
import { CopyWithCoinPrice, COIN_PRICE_SLOT } from "@/components/common/CoinPrice";

// The single gated-content upsell sheet. Reuses the existing subscribe flow:
// "Join" -> /pricing (Subscribe.jsx, the real plans flow), "already a member"
// -> /login. No second path to plans, no invented plans copy. Used by the
// lyrics paywall and the translate paywall so they share one flow. Portaled to
// <body> so no parent layer (e.g. a feed item) can trap it under the bottom menu.
export const MembersOnlySheet = ({ open, onClose, title, body }) => {
  const { t } = useTranslation();
  const { joinCoins } = useJoinPrice();
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose} data-testid="members-only-sheet"
          className="fixed inset-0 z-[10050] flex items-end sm:items-center justify-center px-4"
          style={{ background: "rgba(0,0,0,0.55)" }}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl p-8 text-center"
            style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)" }}
          >
            <Lock className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--accent-primary)" }} />
            <h3 className="text-lg font-bold mb-1" style={{ color: "var(--text-primary)" }}>{title || t("members.title")}</h3>
            <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>{body || t("members.body")}</p>
            <a href="/pricing" data-testid="members-sheet-join" className="btn-pill btn-primary inline-block w-full"><CopyWithCoinPrice template={t("subscribers.join", { price: COIN_PRICE_SLOT })} coins={joinCoins} size="sm" /></a>
            <a href="/login" data-testid="members-sheet-login" className="block mt-3 text-sm font-medium" style={{ color: "var(--text-secondary)" }}>{t("watch.wallLogin")}</a>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default MembersOnlySheet;
